import { useAuth0 } from "@auth0/auth0-react";
import {
  EventStreamContentType,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { useEffect, useRef } from "react";

import { getBackendAPI, USE_AUTH } from "../api/apiConfig";

const COALESCE_MS = 1000;
const RESYNC_EVENT = "resync";
const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 5000;

class FatalStreamError extends Error {}

export interface ServerSentChange {
  id: string;
  actorId: string | null;
  actorName: string | null;
}

interface ServerSentPayload {
  ids: string[];
  actor?: { id: string | null; name: string | null };
}

interface UseServerSentEventsOptions {
  practiceIds: string[];
  topics: string[];
  onChange: (changes: ServerSentChange[] | null) => void;
}

export default function useServerSentEvents({
  practiceIds,
  topics,
  onChange,
}: UseServerSentEventsOptions): void {
  const { getAccessTokenSilently } = useAuth0();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const practiceKey = [...practiceIds].sort().join(",");
  const topicKey = [...topics].sort().join(",");

  useEffect(() => {
    if (!practiceKey) return;

    const controller = new AbortController();
    const topicSet = new Set(topicKey.split(","));
    const pending = new Map<string, ServerSentChange>();
    let coalesceTimer: ReturnType<typeof setTimeout> | undefined;
    let hasOpened = false;
    let missedWhileHidden = false;

    const isHidden = () => document.visibilityState === "hidden";

    const notify = (changes: ServerSentChange[] | null) => {
      if (isHidden()) {
        missedWhileHidden = true;
        return;
      }
      onChangeRef.current(changes);
    };

    const flush = () => {
      coalesceTimer = undefined;
      const changes = [...pending.values()];
      pending.clear();
      notify(changes);
    };

    const onVisibilityChange = () => {
      if (isHidden() || !missedWhileHidden) return;
      missedWhileHidden = false;
      onChangeRef.current(null);
    };

    const cleanup = () => {
      clearTimeout(coalesceTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    const query = new URLSearchParams(
      practiceKey.split(",").map((id) => ["practice_id", id]),
    );

    fetchEventSource(`${getBackendAPI()}sse/events?${query}`, {
      signal: controller.signal,
      openWhenHidden: true,
      fetch: async (input, init) => {
        if (!USE_AUTH) return fetch(input, init);
        const token = await getAccessTokenSilently();
        return fetch(input, {
          ...init,
          headers: { ...init?.headers, Authorization: `Bearer ${token}` },
        });
      },
      onopen: async (response) => {
        const contentType = response.headers.get("content-type") ?? "";
        if (response.ok && contentType.startsWith(EventStreamContentType)) {
          if (hasOpened) notify(null);
          hasOpened = true;
          return;
        }
        if (
          response.status === 204 ||
          (response.status >= 400 &&
            response.status < 500 &&
            response.status !== 401)
        ) {
          throw new FatalStreamError(`SSE stopped: ${response.status}`);
        }
        throw new Error(`SSE open failed: ${response.status}`);
      },
      onmessage: (message) => {
        if (message.event === RESYNC_EVENT) {
          notify(null);
          return;
        }
        if (!topicSet.has(message.event)) return;
        const { ids, actor } = JSON.parse(message.data) as ServerSentPayload;
        for (const id of ids) {
          pending.set(id, {
            id,
            actorId: actor?.id ?? null,
            actorName: actor?.name ?? null,
          });
        }
        if (!coalesceTimer) coalesceTimer = setTimeout(flush, COALESCE_MS);
      },
      onclose: () => {
        throw new Error("SSE stream closed");
      },
      onerror: (error) => {
        if (error instanceof FatalStreamError) throw error;
        return RETRY_MIN_MS + Math.random() * (RETRY_MAX_MS - RETRY_MIN_MS);
      },
    }).catch(cleanup);

    return () => {
      controller.abort();
      cleanup();
    };
  }, [practiceKey, topicKey, getAccessTokenSilently]);
}
